/**
 * TriShellLayout — vibe-designer 1.168.0 Phase 1.
 *
 * Extends DualShellLayout's E.21.24 pattern (keep both shells mounted,
 * toggle via display:contents/none) to 3 shells: Designer / Knowledge /
 * Terminals. The display:contents approach preserves:
 *   - xterm DOM + scrollback inside TerminalsShell
 *   - WebSocket connections to /api/ttyd-proxy/{service}
 *   - server-side ttyd PTY sessions (the proxy cancels its pumps on WS close)
 *   - voice tabs' per-tab byTabId state inside AppShell (voice arc 1.165–1.167)
 *
 * Replaces DualShellLayout.tsx (removed in the same commit) — only two
 * source imports referenced it (App.tsx + its own test).
 *
 * Trade-off (carried over from DualShellLayout): all 3 shells render their
 * first-mount effects even before the user visits Knowledge or Terminals.
 * The KnowledgeShell Phase 1 placeholder is ~40 lines with no effects, so
 * this cost is bounded.
 */
import type { ReactNode } from 'react';
import type { ViewMode } from '@/stores/uiStore';

interface TriShellLayoutProps {
  viewMode: ViewMode;
  // Dashboard tab P1 (§11 LOCKED 2026-07-28) — leftmost/front-door shell hosting
  // the user-facing onboarding "Business OS" (DashboardShell). Always-mounted
  // like the others so the onboarding wizard's in-progress state survives tab
  // switches. Design doc:
  // docs/20260728/dashboard_onboarding_design/20260728_DESIGN_DASHBOARD_TAB_ONBOARDING_BUSINESS_OS.md
  dashboard: ReactNode;
  designer: ReactNode;
  knowledge: ReactNode;
  terminals: ReactNode;
  // Briefing Room M1 (2026-05-31) — 4th shell hosting loadable vibe-designer
  // modules. ModuleHostPane (placeholder in M1; lazy-loaded module SPA in
  // M3+) renders here. Always-mounted per the display:contents/none strategy
  // so future module SPAs preserve their state across tab switches.
  modules: ReactNode;
  // 2026-06-26 — 5th shell: the Deploys admin view (manage + undeploy this
  // tenant's deployed projects). Always-mounted like the others.
  deploys: ReactNode;
}

export function TriShellLayout({
  viewMode,
  dashboard,
  designer,
  knowledge,
  terminals,
  modules,
  deploys,
}: TriShellLayoutProps) {
  return (
    <>
      <div
        data-testid="dashboard-shell"
        style={{
          display: viewMode === 'dashboard' ? 'contents' : 'none',
        }}
      >
        {dashboard}
      </div>
      <div
        data-testid="designer-shell"
        style={{
          display: viewMode === 'designer' ? 'contents' : 'none',
        }}
      >
        {designer}
      </div>
      <div
        data-testid="knowledge-shell"
        style={{
          display: viewMode === 'knowledge' ? 'contents' : 'none',
        }}
      >
        {knowledge}
      </div>
      <div
        data-testid="terminals-shell"
        style={{
          display: viewMode === 'terminals' ? 'contents' : 'none',
        }}
      >
        {terminals}
      </div>
      <div
        data-testid="modules-shell"
        style={{
          display: viewMode === 'modules' ? 'contents' : 'none',
        }}
      >
        {modules}
      </div>
      <div
        data-testid="deploys-shell"
        style={{
          display: viewMode === 'deploys' ? 'contents' : 'none',
        }}
      >
        {deploys}
      </div>
    </>
  );
}
